import {
  useAddToWishlistMutation,
  useChangeWishlistItemQuantityMutation,
  useGetWishlistQuery,
  useRemoveFromWishlistMutation,
} from '@api/api';
import { Price, WishlistLineItem } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { useToast } from '@hooks';
import { useCart } from '@hooks/cart/cart.hooks';
import { t } from '@translations';
import { getImageResizerUrl } from '@utils/images.utils';
import debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';

export const useWishlist = () => {
  const { currentData, isError, isFetching, isLoading, isSuccess } = useGetWishlistQuery();
  const getWishlistStatus = {
    isError,
    isFetching,
    isLoading,
    isSuccess,
  };
  const [addToWishlist, addResponse] = useAddToWishlistMutation();
  const [removeFromWishlist, removeResponse] = useRemoveFromWishlistMutation();
  const [changeWishlistQuantity, changeQuantityResponse] = useChangeWishlistItemQuantityMutation();
  const { getErrorHandler, getAddSuccessHandler, getRemoveHandler } = useWishlistMessageHandlers();

  const addToWishlistHandler = async (code: string, name?: string) => {
    await addToWishlist({
      code,
    })
      .unwrap()
      .then(getAddSuccessHandler(name), getErrorHandler());
  };

  const removeFromWishlistHandler = async (code: string, name?: string) => {
    await removeFromWishlist({ code }).then(getRemoveHandler(name), getErrorHandler());
  };

  const changeWishlistQuantityHandler = ({
    quantity,
    code,
  }: {
    quantity: number;
    code: string;
  }) => {
    changeWishlistQuantity({ code, quantity });
  };

  return {
    addToWishlistHandler,
    removeFromWishlistHandler,
    changeWishlistQuantityHandler,
    addResponse,
    removeResponse,
    changeQuantityResponse,
    wishlist: currentData?.data?.lineItems,
    getWishlistStatus,
  };
};

export const useWishlistItem = (lineItem: WishlistLineItem) => {
  const { name, code = '', imageUrl = '', quantity = 0, placedPrice } = lineItem;
  const [trigger] = useChangeWishlistItemQuantityMutation();
  const [localQuantity, setLocalQuantity] = useState<number>(quantity);
  const resizedImageUrl = getImageResizerUrl(imageUrl, { width: 100, format: 'webp' });
  const itemPrice = placedPrice ?? ({} as Price);
  const totalItemPrice = { ...itemPrice, amount: itemPrice.amount * quantity } as Price;

  const { addToCart, addToCartStatus } = useCart();
  const { isLoading } = addToCartStatus;
  const isAddToCartWorking = isLoading;

  useEffect(() => {
    setLocalQuantity(quantity);
  }, [quantity]);

  // debounce only once
  const debouncedTriggerRef = useRef(
    debounce((newQuantity: number, codeParam: string, prevQuantity: number) => {
      if (newQuantity !== prevQuantity) {
        trigger({ code: codeParam, quantity: newQuantity });
      }
    }, 1000)
  );

  useEffect(() => {
    debouncedTriggerRef.current(localQuantity, code, quantity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQuantity]);

  useEffect(() => {
    const debouncedTrigger = debouncedTriggerRef.current;
    return () => {
      debouncedTrigger.cancel();
    };
  }, []);

  const setLocalQuantityHandler = (value: number) => {
    setLocalQuantity(value);
  };

  const addToCartHandler = () => {
    addToCart(code, quantity, name);
  };

  return {
    localQuantity,
    setLocalQuantityHandler,
    addToCartHandler,
    isAddToCartWorking,
    resizedImageUrl,
    itemPrice,
    totalItemPrice,
  };
};

const useWishlistMessageHandlers = () => {
  const { toast } = useToast();
  const getErrorHandler = () => () =>
    toast({
      severity: 'error',
      message: t(TextCodes.Product.Wishlist.Error),
    });

  const getAddSuccessHandler =
    (name: string = '') =>
    () =>
      toast({
        severity: 'success',
        message: t(TextCodes.Product.Wishlist.Success, { name }),
      });
  const getRemoveHandler =
    (name: string = '') =>
    () =>
      toast({
        severity: 'success',
        message: t(TextCodes.Product.Wishlist.Removed, { name }),
      });

  return { getAddSuccessHandler, getErrorHandler, getRemoveHandler };
};
